import { Card, Flex, Row, Space, Typography } from 'antd';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOrganizationConfigs } from '../../../api/Onboarding';
import useAuth from '../../../auth/useAuth';
import ChartExportMenu from '../../../components/ChartExportMenu';
import { ConfigKey } from '../../../constants';
import { getMultiGridThemesChart } from '../../../echarts/ChartHelper';
import useParams from '../../../hooks/useParams';
import { Gray7, defaultChartColors } from '../../../styles';
import { queryType, questionThemeType, questionType, segmentType } from '../../../types';

const { Title } = Typography;

const LegendColor = styled.div`
  width: 11px;
  height: 11px;
  margin-left: 14px;
  background-color: ${(props) => props.$color};
`;

const StyledTitle = styled(Title)`
  text-align: center;
`;

const StyledLegendText = styled.span`
  font-size: 15px;
  color: ${Gray7};
`;

/**
 * Compute how many segment charts fit per row based on container width.
 */
const getMaxPerRow = (width) => {
  if (width >= 1200) return 4;
  if (width >= 900) return 3;
  if (width >= 600) return 2;
  return 1;
};

function ThemesChart({
  question,
  themes,
  activeSegments,
  segmentThemes,
  chartValueType,
  hiddenThemes,
  highlightThemeIds,
}) {
  const chartRef = useRef();
  const containerRef = useRef();
  const { surveyId } = useParams();
  const { user } = useAuth();
  const [rowCharts, setRowCharts] = useState([]); // Array of multi-grid charts, one per row
  const [containerWidth, setContainerWidth] = useState(1200);

  // Track container width via ResizeObserver for responsive column count
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data: configs } = useOrganizationConfigs(
    { organizationId: user.organization_id },
    { enabled: !!user.organization_id },
  );

  const sortThemes = useCallback(
    (targetThemes) => {
      const groupOrder = themes.map((t) => t.data.id);
      const themeOrderMap = themes.reduce((acc, t) => {
        acc[t.data.id] = t.children.map((child) => child.data.id);
        return acc;
      }, {});

      // Sort groups
      const sortedThemes = [...targetThemes].sort(
        (a, b) => groupOrder.indexOf(a.data.id) - groupOrder.indexOf(b.data.id),
      );

      // Sort themes within each group
      sortedThemes.forEach((t) => {
        if (themeOrderMap[t.data.id]) {
          t.children.sort(
            (a, b) =>
              themeOrderMap[t.data.id].indexOf(a.data.id) -
              themeOrderMap[t.data.id].indexOf(b.data.id),
          );
        }
      });

      return sortedThemes;
    },
    [themes],
  );

  const legendItems = [
    {
      color:
        configs?.data[ConfigKey.PRIMARY_CHART_COLOR] ||
        defaultChartColors[ConfigKey.PRIMARY_CHART_COLOR],
      label: 'Group',
    },
    {
      color:
        configs?.data[ConfigKey.SECONDARY_CHART_COLOR] ||
        defaultChartColors[ConfigKey.SECONDARY_CHART_COLOR],
      label: 'Theme',
    },
  ];

  const getSegmentParams = (segmentId, segment) => {
    if (!segment) {
      return null;
    }
    // we need to construct a separate set of search params where `+` and `&` are replaced
    // if we don't, the browser misinterprets those special characters
    // and we can't recover the filters
    const encodedParams = new URLSearchParams({ segmentId });
    Object.keys(segment).forEach((key) => {
      if (segment[key]?.length) {
        encodedParams.append(
          key,
          segment[key]
            .join('_$SEPARATOR$_')
            .replaceAll('+', '_$PLUS_')
            .replaceAll('&', '_$AMPERSAND_'),
        );
      }
    });
    return encodedParams;
  };

  useEffect(() => {
    if (!activeSegments.length) {
      setRowCharts([]);
      return;
    }

    // Prepare segment data with themes
    const preparedSegments = activeSegments.map((s) => {
      const { data } = segmentThemes.find((t) => t.data.segmentId === s.id);
      // Filter out themes that are not selected for display
      const visibleThemes = data.themes
        .filter((t) => !hiddenThemes.includes(t.data.id))
        .map((t) => {
          const children = t.children.filter((c) => !hiddenThemes.includes(c.data.id));
          return { ...t, children };
        });

      const reorderedThemes = sortThemes(visibleThemes);
      return {
        id: s.id,
        label: s.label,
        themes: reorderedThemes,
        responseCount: data.response_count,
        filters: getSegmentParams(s.id, s.segment),
      };
    });

    // Compute responsive max segments per row from container width
    const maxPerRow = getMaxPerRow(containerWidth);
    const rows = [];
    for (let i = 0; i < preparedSegments.length; i += maxPerRow) {
      rows.push(preparedSegments.slice(i, i + maxPerRow));
    }

    // Determine sizing strategy:
    // - Single row (1-4 segments): use dynamic sizing based on actual count
    // - Multiple rows (5+ segments): use consistent sizing (maxPerRow) across all rows
    const hasMultipleRows = rows.length > 1;

    // Create a multi-grid chart for each row
    const charts = rows.map((rowSegments, rowIdx) => ({
      ...getMultiGridThemesChart({
        segments: rowSegments,
        questionId: question.id,
        surveyId,
        chartValueType,
        highlightThemeIds,
        organizationConfigs: configs,
        // Use maxPerRow for consistent sizing when multiple rows, otherwise use actual segment count
        maxSegmentsPerRow: hasMultipleRows ? maxPerRow : rowSegments.length,
        containerWidth,
      }),
      // Add unique key based on segment IDs to force re-render
      key: `row-${rowIdx}-${rowSegments.map((s) => s.id).join('-')}-${rowSegments.length}`,
    }));

    setRowCharts(charts);
  }, [
    segmentThemes,
    activeSegments,
    chartValueType,
    question,
    surveyId,
    hiddenThemes,
    highlightThemeIds,
    sortThemes,
    configs,
    containerWidth,
  ]);

  // Handle chart click events for navigation
  const handleChartClick = (chartConfig) => (params) => {
    if (!chartConfig.clickable || !chartConfig.links) return;
    const linkInfo = chartConfig.links.find(
      (l) => l.themeId === params.data.themeId && l.segmentIdx === params.data.segmentIdx,
    );
    if (linkInfo) {
      window.location.href = linkInfo.link;
    }
  };

  const getCsvData = useCallback(() => {
    const rows = [];
    activeSegments.forEach((s) => {
      const segmentData = segmentThemes.find((t) => t.data.segmentId === s.id);
      if (!segmentData) return;
      const visibleThemes = segmentData.data.themes
        .filter((t) => !hiddenThemes.includes(t.data.id))
        .map((t) => ({
          ...t,
          children: t.children.filter((c) => !hiddenThemes.includes(c.data.id)),
        }));
      const reorderedThemes = sortThemes(visibleThemes);
      reorderedThemes.forEach((t) => {
        rows.push({
          Segment: s.label,
          Type: 'Group',
          Name: t.data.name,
          'Percentage (%)': t.data.percentage,
          'Response Count': t.data.response_count,
        });
        t.children.forEach((c) => {
          rows.push({
            Segment: s.label,
            Type: 'Theme',
            Name: c.data.name,
            'Percentage (%)': c.data.percentage,
            'Response Count': c.data.response_count,
          });
        });
      });
    });
    return rows;
  }, [activeSegments, segmentThemes, hiddenThemes, sortThemes]);

  return (
    <Card size="small">
      <div
        ref={(node) => {
          chartRef.current = node;
          containerRef.current = node;
        }}
      >
        <Flex vertical gap={16}>
          <Row justify="center">
            <StyledTitle level={5}>{question.text}</StyledTitle>
          </Row>
          {rowCharts.length > 0 && (
            <Row justify="end">
              <ChartExportMenu chartRef={chartRef} filename={question.text} csvData={getCsvData} />
            </Row>
          )}
          {rowCharts.map((chart) => (
            <div
              key={chart.key}
              className="hide-chart-caption"
              data-cy="themes-chart"
              data-chart-links={JSON.stringify(chart.links || [])}
            >
              <ReactECharts
                option={chart.option}
                style={{ height: chart.height, width: '100%' }}
                opts={{ renderer: 'svg' }}
                onEvents={{ click: handleChartClick(chart) }}
                notMerge
                lazyUpdate={false}
              />
            </div>
          ))}
          <Row justify="center">
            <Space size={7}>
              {legendItems.map((item) => (
                <React.Fragment key={item.label}>
                  <LegendColor $color={item.color} />
                  <StyledLegendText>{item.label}</StyledLegendText>
                </React.Fragment>
              ))}
            </Space>
          </Row>
        </Flex>
      </div>
    </Card>
  );
}

ThemesChart.propTypes = {
  question: questionType.isRequired,
  themes: PropTypes.arrayOf(questionThemeType).isRequired,
  activeSegments: PropTypes.arrayOf(segmentType).isRequired,
  segmentThemes: PropTypes.arrayOf(queryType).isRequired,
  chartValueType: PropTypes.string.isRequired,
  hiddenThemes: PropTypes.arrayOf(PropTypes.number).isRequired,
  highlightThemeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default ThemesChart;
